import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useBusinessProfiles, type BusinessProfile } from '../../hooks/useBusinessProfiles';
import { useGenerationCount } from '../../hooks/useGenerationCount';
import { useUserPlan, PLAN_BATCH_OPTIONS } from '../../hooks/useUserPlan';
import { useCreditsContext } from '../../context/CreditsContext';
import { fetchGeneratedAds, QuotaExceededError, InsufficientCreditsError, ProBulkCapError, ProBulkCooldownError } from '../../api/generateAds';
import { useAdSession } from '../../hooks/useAdSession';
import { useCreativePresets, type CreativePreset, type ImageAdPresetSettings } from '../../hooks/useCreativePresets';
import { getSupabaseFunctionUrl, supabase } from '../../lib/supabase';
import ResultsPanel from '../dashboard/components/ResultsPanel';
import StylePickerModal from '../dashboard/components/StylePickerModal';
import { adPlatforms, toneOptions } from '../../mocks/exampleAds';
import type { AdGeneration } from '../../hooks/useAdHistory';
import type { AdResult } from '../../mocks/adResults';
import { IMAGE_ENGINES, getEngineConfig, getBulkCreditCost, type ImageEngineId } from '../../config/imageEngines';
import { IMAGE_STYLES, getImageStyleConfig } from '../../config/imageStyles';
import { getEdgeFunctionHeaders } from '../../lib/edgeFunctions';
import { trackEvent } from '../../lib/analytics';

const REGEN_API_URL = getSupabaseFunctionUrl('regenerate-single-image');
const REGEN_COPY_API_URL = getSupabaseFunctionUrl('regenerate-copy');

interface CampaignForm {
  offer: string;
  campaignGoal: string;
  audienceOverride: string;
  platform: string;
  toneOverride: string;
  adAngle: string;
  campaignNotes: string;
  creativeDirection: string;
  stylePackIds: string[];
  imageCount: number;
}

const defaultCampaign: CampaignForm = {
  offer: '',
  campaignGoal: '',
  audienceOverride: '',
  platform: 'Facebook',
  toneOverride: '',
  adAngle: '',
  campaignNotes: '',
  creativeDirection: '',
  stylePackIds: [],
  imageCount: 1,
};

const campaignGoals = [
  'Generate leads', 'Drive website traffic', 'Boost sales / conversions',
  'Build brand awareness', 'Promote a sale or event', 'App downloads', 'Retarget past visitors',
];

const adAngles = [
  { value: 'Problem/Solution', label: 'Problem / Solution', icon: 'ri-question-answer-line', desc: 'Identify the pain, offer the fix' },
  { value: 'Before/After', label: 'Before / After', icon: 'ri-contrast-2-line', desc: 'Show the transformation' },
  { value: 'Offer-driven', label: 'Offer-driven', icon: 'ri-price-tag-3-line', desc: 'Lead with the deal' },
  { value: 'Emotional', label: 'Emotional', icon: 'ri-heart-line', desc: 'Connect through feeling' },
  { value: 'Authority/Expert', label: 'Authority / Expert', icon: 'ri-shield-star-line', desc: 'Build trust with expertise' },
];

/* ─── Types ───────────────────────────────────────────────────────────────── */
interface RegenParams {
  businessName: string;
  businessType: string;
  businessDescription?: string;
  servicesOffered?: string;
  offer: string;
  audience?: string;
  tone?: string;
  creativeDirection?: string;
}

/* ─── Collapsible Section ─────────────────────────────────────────────────── */
function CollapsibleSection({
  icon,
  title,
  subtitle,
  badge,
  defaultOpen = false,
  children,
}: {
  icon: string;
  title: string;
  subtitle: string;
  badge?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-stone-200 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3.5 bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer text-left"
      >
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-stone-200 shrink-0">
          <i className={`${icon} text-stone-500 text-sm`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-stone-800">{title}</span>
            {badge !== undefined && badge > 0 && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-500 text-white text-xs font-bold leading-none">
                {badge}
              </span>
            )}
          </div>
          <span className="text-xs text-stone-400">{subtitle}</span>
        </div>
        <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-stone-400 text-lg shrink-0 transition-transform duration-200`} />
      </button>
      {open && (
        <div className="px-4 py-4 bg-white border-t border-stone-100 space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}

/* ─── Profile Selector ────────────────────────────────────────────────────── */
function ProfileSelector({
  profiles,
  selectedId,
  onSelect,
  loading,
}: {
  profiles: BusinessProfile[];
  selectedId: string;
  onSelect: (id: string) => void;
  loading: boolean;
}) {
  const selected = profiles.find(p => p.id === selectedId);

  if (loading) {
    return <div className="bg-white rounded-2xl border border-stone-100 p-4 animate-pulse h-20" />;
  }

  if (profiles.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-dashed border-stone-200 px-5 py-5 flex items-center gap-4">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-100 shrink-0">
          <i className="ri-building-2-line text-stone-400 text-lg" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-stone-800">No Business Profiles found</p>
          <p className="text-xs text-stone-400 mt-0.5">Create a profile to auto-fill your business info in every campaign.</p>
        </div>
        <Link
          to="/dashboard/profiles/new"
          className="shrink-0 flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Create Profile
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-4">
      <div className="flex items-center gap-2 mb-3">
        <i className="ri-building-2-line text-orange-400 text-sm" />
        <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">Generating for</span>
      </div>
      {profiles.length === 1 ? (
        <div className="flex items-center gap-3 bg-orange-50 border border-orange-100 rounded-xl px-4 py-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 to-amber-50 shrink-0">
            <i className="ri-building-2-line text-orange-500 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-stone-900">{profiles[0].business_name}</div>
            <div className="text-xs text-stone-500">{profiles[0].business_type} {profiles[0].location ? `· ${profiles[0].location}` : ''}</div>
          </div>
          <i className="ri-check-line text-orange-400 shrink-0" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {profiles.map(p => (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              className={`flex items-center gap-3 rounded-xl px-4 py-3 border transition-all duration-200 cursor-pointer text-left ${
                selectedId === p.id
                  ? 'bg-orange-50 border-orange-200 text-orange-700'
                  : 'bg-stone-50 border-stone-200 text-stone-700 hover:border-orange-200 hover:bg-orange-50/50'
              }`}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-orange-100 to-amber-50 shrink-0">
                <i className="ri-building-2-line text-orange-500 text-xs" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold truncate">{p.business_name}</div>
                <div className="text-xs text-stone-400 truncate">{p.business_type}</div>
              </div>
              {selectedId === p.id && <i className="ri-check-line text-orange-400 text-xs shrink-0" />}
            </button>
          ))}
        </div>
      )}
      {selected && (
        <Link
          to={`/dashboard/profiles/${selected.id}/edit`}
          className="mt-2 flex items-center gap-1 text-xs text-stone-400 hover:text-orange-500 transition-colors"
        >
          <i className="ri-edit-line text-xs" /> Edit profile
        </Link>
      )}
    </div>
  );
}

/* ─── Convert history item → AdResult ───────────────────────────────────── */
function historyItemToAdResult(item: AdGeneration): AdResult {
  return {
    hooks: item.hooks ?? [],
    headlines: item.headlines ?? [],
    subheadlines: item.subheadlines ?? [],
    primaryText: item.primary_text ?? [],
    ctas: item.ctas ?? [],
    overlayText: item.overlay_text ?? undefined,
    imageUrls: item.image_storage_urls ?? [],
    imageStorageUrls: item.image_storage_urls ?? [],
  };
}

/* ─── Main Page ───────────────────────────────────────────────────────────── */
export default function GeneratePage() {
  const location = useLocation();
  const { profiles, loading: profilesLoading } = useBusinessProfiles();
  const { remaining, loading: countLoading, refetch: refetchCount } = useGenerationCount();
  const { generationLimit, adsPerBatch, plan } = useUserPlan();

  const {
    result,
    setResult,
    updateResultCopy,
    editedUrls,
    updateEditedUrl,
    editHistory,
    updateEditHistory,
    revertToOriginal,
    clearSession,
    sessionLoaded,
    generationId,
    setGenerationId,
  } = useAdSession();

  const { refetch: refetchCredits, fireToast, creditsRemaining, hasEnoughCredits } = useCreditsContext();
  const {
    presets: creativePresets,
    loading: presetsLoading,
    error: presetsError,
    createPreset,
    deletePreset,
  } = useCreativePresets<ImageAdPresetSettings>('image_ad');

  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [campaign, setCampaign] = useState<CampaignForm>(defaultCampaign);
  const [imageEngine, setImageEngine] = useState<ImageEngineId>('standard');
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetMessage, setPresetMessage] = useState<string | null>(null);
  const [savingPreset, setSavingPreset] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  const [proBulkCapExceeded, setProBulkCapExceeded] = useState(false);
  const [proBulkCooldown, setProBulkCooldown] = useState<{ remainingMin: number } | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [loadedFromHistory, setLoadedFromHistory] = useState(false);
  const [styleModalOpen, setStyleModalOpen] = useState(false);

  // Freeze the business name at generation time so switching profiles doesn't change it in the results
  const [generatedBusinessName, setGeneratedBusinessName] = useState<string | null>(null);

  // Reference image for style transfer
  const [referenceImageData, setReferenceImageData] = useState<string | null>(null);
  const [referenceImagePreview, setReferenceImagePreview] = useState<string | null>(null);
  const referenceImageInputRef = useRef<HTMLInputElement>(null);

  // Store the params from the last generation so single-regen can use them
  const lastRegenParamsRef = useRef<RegenParams | null>(null);
  const generationAbortRef = useRef<AbortController | null>(null);

  // ── Handle incoming history item from history page ──────────────────────
  useEffect(() => {
    const state = location.state as { profileId?: string; historyItem?: AdGeneration } | null;
    const historyItem = state?.historyItem;
    if (historyItem && sessionLoaded) {
      const adResult = historyItemToAdResult(historyItem);
      setResult(adResult);
      setGenerationId(historyItem.id);
      setLoadedFromHistory(true);
      // Set platform from history item
      if (historyItem.platform) {
        setCampaign(prev => ({ ...prev, platform: historyItem.platform! }));
      }
      // Freeze the business name shown in the results for this history item
      if (historyItem.business_name) {
        setGeneratedBusinessName(historyItem.business_name);
      }
      // Clear navigation state so a page refresh doesn't reload the item
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.state, sessionLoaded]);

  useEffect(() => {
    const stateProfileId = (location.state as { profileId?: string } | null)?.profileId;
    if (stateProfileId) {
      setSelectedProfileId(stateProfileId);
    } else if (profiles.length > 0 && !selectedProfileId) {
      setSelectedProfileId(profiles[0].id);
    }
  }, [profiles, location.state]);

  useEffect(() => {
    return () => generationAbortRef.current?.abort();
  }, []);

  const selectedProfile = profiles.find(p => p.id === selectedProfileId);
  const selectedPreset = creativePresets.find((preset) => preset.id === selectedPresetId);

  const handleChange = (field: keyof CampaignForm, value: string) => {
    setCampaign(prev => ({ ...prev, [field]: value }));
  };

  const buildPresetSettings = (): ImageAdPresetSettings => ({
    campaign,
    imageEngine,
    referenceImageData,
    referenceImagePreview,
  });

  const applyPreset = (preset: CreativePreset<ImageAdPresetSettings>) => {
    const settings = preset.settings;
    setCampaign({
      ...defaultCampaign,
      ...settings.campaign,
      stylePackIds: Array.isArray(settings.campaign.stylePackIds) ? settings.campaign.stylePackIds : [],
      imageCount: settings.campaign.imageCount || 1,
    });
    if (IMAGE_ENGINES.some((engine) => engine.id === settings.imageEngine)) {
      setImageEngine(settings.imageEngine as ImageEngineId);
    }
    setReferenceImageData(settings.referenceImageData ?? null);
    setReferenceImagePreview(settings.referenceImagePreview ?? null);
    if (preset.business_profile_id && profiles.some((profile) => profile.id === preset.business_profile_id)) {
      setSelectedProfileId(preset.business_profile_id);
    }
    setPresetMessage(`Loaded "${preset.name}".`);
  };

  const savePreset = async () => {
    if (savingPreset) return;
    const cleanName = presetName.trim();
    if (!cleanName) {
      setPresetMessage('Name the preset before saving it.');
      return;
    }
    setSavingPreset(true);
    setPresetMessage(null);
    const { error: saveErr } = await createPreset({
      name: cleanName,
      businessProfileId: selectedProfileId || null,
      settings: buildPresetSettings(),
    });
    setSavingPreset(false);
    if (saveErr) {
      setPresetMessage(saveErr);
      return;
    }
    setPresetName('');
    setShowSavePreset(false);
    setPresetMessage(`Saved "${cleanName}" as a reusable preset.`);
  };

  /** Compress uploaded reference image to max 512px JPEG to keep payload small */
  const compressImage = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const MAX = 512;
        let w = img.width;
        let h = img.height;
        if (w > h) {
          if (w > MAX) { h = Math.round(h * MAX / w); w = MAX; }
        } else {
          if (h > MAX) { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { URL.revokeObjectURL(url); reject(new Error('Canvas unavailable')); return; }
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
        resolve(dataUrl.split(',')[1]);
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
      img.src = url;
    });
  }, []);

  const handleReferenceImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setReferenceImageData(compressed);
      setReferenceImagePreview(`data:image/jpeg;base64,${compressed}`);
    } catch {
      // silently ignore compression errors
    }
    e.target.value = '';
  }, [compressImage]);

  const isValid = Boolean(selectedProfile && campaign.platform);

  const stopGeneration = useCallback(() => {
    generationAbortRef.current?.abort();
    generationAbortRef.current = null;
    setIsGenerating(false);
    setError('Generation stopped. Update your form and try again when you are ready.');
  }, []);

  const run = async () => {
    if (!isValid || isGenerating || !selectedProfile) return;
    const abortController = new AbortController();
    generationAbortRef.current = abortController;
    setIsGenerating(true);
    setResult(null);
    setError(null);
    setQuotaExceeded(false);
    setInsufficientCredits(false);
    setProBulkCapExceeded(false);
    setProBulkCooldown(null);
    setShowClearConfirm(false);
    // Freeze the business name for this generation
    setGeneratedBusinessName(selectedProfile.business_name);

    // Save params for per-image regeneration
    lastRegenParamsRef.current = {
      businessName: selectedProfile.business_name,
      businessType: selectedProfile.business_type,
      businessDescription: selectedProfile.business_description,
      servicesOffered: selectedProfile.services_offered,
      offer: campaign.offer,
      audience: campaign.audienceOverride || selectedProfile.default_audience,
      tone: campaign.toneOverride || selectedProfile.default_tone,
      creativeDirection: campaign.creativeDirection || undefined,
    };

    try {
      const adResult = await fetchGeneratedAds(
        {
          businessName: selectedProfile.business_name,
          businessType: selectedProfile.business_type,
          businessDescription: selectedProfile.business_description,
          audience: campaign.audienceOverride || selectedProfile.default_audience,
          offer: campaign.offer,
          platform: campaign.platform,
          tone: campaign.toneOverride || selectedProfile.default_tone,
          campaignGoal: campaign.campaignGoal,
          campaignNotes: campaign.campaignNotes,
          businessProfileId: selectedProfile.id,
          servicesOffered: selectedProfile.services_offered,
          location: selectedProfile.location,
          brandPositioning: selectedProfile.brand_positioning,
          uniqueDifferentiator: selectedProfile.unique_differentiator,
          adAngle: campaign.adAngle || undefined,
          creativeDirection: campaign.creativeDirection || undefined,
          stylePack: campaign.stylePackIds[0] || 'auto',
          stylePackIds: campaign.stylePackIds.length > 0 ? campaign.stylePackIds : undefined,
          imageCount: plan !== 'free' ? effectiveCount : undefined,
          referenceImageBase64: referenceImageData ?? undefined,
          imageEngine,
          provider: getEngineConfig(imageEngine).provider,
        },
        { signal: abortController.signal }
      );
      if (abortController.signal.aborted || generationAbortRef.current !== abortController) return;
      // Attach the style IDs used so the results panel can show correct labels
      // If user picked explicit styles, use those. Otherwise use the auto-selected IDs
      // returned by the edge function so the results panel shows the real style name.
      const resultWithStyles = campaign.stylePackIds.length > 0
        ? { ...adResult, stylePackIds: campaign.stylePackIds }
        : adResult.autoSelectedStyleIds && adResult.autoSelectedStyleIds.length > 0
          ? { ...adResult, stylePackIds: adResult.autoSelectedStyleIds }
          : adResult;
      setResult(resultWithStyles);
      setGenerationId(adResult.generationId ?? null);
      await refetchCount();
      await refetchCredits();
      fireToast('Ads generated successfully', 'deduct', effectiveCreditCost);
      trackEvent('first_ad_generated', {
        source: 'dashboard_generate',
        credits_used: effectiveCreditCost,
        images_generated: effectiveCount,
      });
      trackEvent('credits_used', {
        source: 'dashboard_generate',
        action: 'generate_ad',
        amount: effectiveCreditCost,
      });
    } catch (err) {
      if (abortController.signal.aborted) {
        if (generationAbortRef.current === abortController) {
          setError('Generation stopped. Update your form and try again when you are ready.');
        }
      } else if (err instanceof InsufficientCreditsError) {
        setInsufficientCredits(true);
      } else if (err instanceof QuotaExceededError) {
        setQuotaExceeded(true);
      } else if (err instanceof ProBulkCapError) {
        setProBulkCapExceeded(true);
      } else if (err instanceof ProBulkCooldownError) {
        setProBulkCooldown({ remainingMin: (err as ProBulkCooldownError).remainingMin });
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong.');
      }
    } finally {
      if (generationAbortRef.current === abortController) {
        generationAbortRef.current = null;
        setIsGenerating(false);
      }
    }
  };

  /** Re-roll a single creative slot without touching the other two */
  const handleRegenerateSingle = async (creativeIndex: number): Promise<void> => {
    // Use stored regen params, or fall back to current profile + campaign state
    const params: RegenParams | null = lastRegenParamsRef.current ?? (
      selectedProfile
        ? {
            businessName: selectedProfile.business_name,
            businessType: selectedProfile.business_type,
            businessDescription: selectedProfile.business_description,
            servicesOffered: selectedProfile.services_offered,
            offer: campaign.offer,
            audience: campaign.audienceOverride || selectedProfile.default_audience,
            tone: campaign.toneOverride || selectedProfile.default_tone,
            creativeDirection: campaign.creativeDirection || undefined,
          }
        : null
    );

    if (!params) {
      throw new Error('Please select a business profile before re-rolling images.');
    }

    const response = await fetch(REGEN_API_URL, {
      method: 'POST',
      headers: await getEdgeFunctionHeaders(),
      body: JSON.stringify({
        creativeIndex,
        businessName: params.businessName,
        businessType: params.businessType,
        businessDescription: params.businessDescription ?? '',
        servicesOffered: params.servicesOffered ?? '',
        offer: params.offer,
        audience: params.audience ?? '',
        tone: params.tone ?? '',
        creativeDirection: params.creativeDirection ?? '',
      }),
    });

    if (response.status === 403) {
      const body = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new InsufficientCreditsError(typeof body.creditsRemaining === 'number' ? body.creditsRemaining : 0);
    }
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);

    const data = await response.json() as Record<string, unknown>;
    if (data?.error) throw new Error(String(data.error));
    if (!data?.imageUrl) throw new Error('No image returned');

    updateEditedUrl(creativeIndex, data.imageUrl as string);
    updateEditHistory(creativeIndex, []);

    // ── Persist the new URL to the DB record so history reflects it ──────────
    if (generationId) {
      const baseUrls = [...(result?.imageStorageUrls ?? result?.imageUrls ?? [])];
      // Apply all existing edits on top of originals
      Object.entries(editedUrls).forEach(([idx, url]) => { baseUrls[Number(idx)] = url; });
      // Now apply the freshly regenerated slot
      baseUrls[creativeIndex] = data.imageUrl as string;
      await supabase
        .from('ad_generations')
        .update({ image_storage_urls: baseUrls })
        .eq('id', generationId);
    }

    await refetchCredits();
    fireToast('Image regenerated', 'deduct', 10);
  };

  /** Regenerate only text copy (hooks, headlines, CTAs) without touching images */
  const handleRegenerateCopy = async (): Promise<void> => {
    if (!selectedProfile && !lastRegenParamsRef.current) {
      throw new Error('Please select a business profile before regenerating copy.');
    }

    const profile = selectedProfile;
    const response = await fetch(REGEN_COPY_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getEdgeFunctionHeaders()),
      },
      body: JSON.stringify({
        businessName: profile?.business_name ?? lastRegenParamsRef.current?.businessName ?? '',
        businessType: profile?.business_type ?? lastRegenParamsRef.current?.businessType ?? '',
        businessDescription: profile?.business_description ?? lastRegenParamsRef.current?.businessDescription ?? '',
        servicesOffered: profile?.services_offered ?? lastRegenParamsRef.current?.servicesOffered ?? '',
        offer: campaign.offer || lastRegenParamsRef.current?.offer || '',
        platform: campaign.platform,
        tone: campaign.toneOverride || profile?.default_tone || '',
        audience: campaign.audienceOverride || profile?.default_audience || '',
        campaignGoal: campaign.campaignGoal || '',
        campaignNotes: campaign.campaignNotes || '',
        adAngle: campaign.adAngle || '',
        brandPositioning: profile?.brand_positioning || '',
        uniqueDifferentiator: profile?.unique_differentiator || '',
        location: profile?.location || '',
        generationId: generationId ?? null,
      }),
    });

    if (response.status === 403) {
      const body = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new InsufficientCreditsError(typeof body.creditsRemaining === 'number' ? body.creditsRemaining : 0);
    }
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);

    const data = await response.json() as Record<string, unknown>;
    if (data.error) throw new Error(String(data.error));

    // Merge new copy into session without wiping image edits
    updateResultCopy({
      hooks: Array.isArray(data.hooks) ? data.hooks as string[] : undefined,
      headlines: Array.isArray(data.headlines) ? data.headlines as string[] : undefined,
      subheadlines: Array.isArray(data.subheadlines) ? data.subheadlines as string[] : undefined,
      primaryText: Array.isArray(data.primaryText) ? data.primaryText as string[] : undefined,
      ctas: Array.isArray(data.ctas) ? data.ctas as string[] : undefined,
      overlayText: (data.overlayText as { headline: string; subheadline: string; cta: string } | undefined),
    });

    await refetchCredits();
    fireToast('Copy regenerated', 'deduct', 5);
  };

  // Derived: batch count and credit cost for the current selection
  const effectiveCount = (plan === 'free' ? 1 : campaign.imageCount) as 1 | 3 | 6;
  const effectiveCreditCost = getBulkCreditCost(imageEngine, effectiveCount);

  const inputClass = 'w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200';
  const isLow = !countLoading && generationLimit && remaining <= 1 && remaining > 0;
  const isGone = !countLoading && generationLimit && remaining === 0;

  // Badge counts for collapsible sections
  const targetingBadge = [campaign.audienceOverride, campaign.toneOverride].filter(Boolean).length;
  const strategyBadge = [campaign.campaignGoal, campaign.adAngle].filter(Boolean).length;
  const creativeBadge = [campaign.creativeDirection, campaign.campaignNotes, campaign.stylePackIds.length > 0 ? 'style' : '', referenceImageData ? 'ref' : ''].filter(Boolean).length;

  return (
    <div className="flex-1 overflow-y-auto">
      <StylePickerModal
        isOpen={styleModalOpen}
        selectedStyleIds={campaign.stylePackIds}
        maxSelections={effectiveCount}
        onSelect={(ids) => setCampaign((prev) => ({ ...prev, stylePackIds: ids }))}
        onClose={() => setStyleModalOpen(false)}
      />
      
      {/* Header */}
      <header className="bg-white border-b border-stone-100 flex items-center justify-between px-4 md:px-6 h-14 md:h-16 shrink-0 sticky top-0 z-10 gap-2">
        <div className="min-w-0 shrink">
          <h1 className="text-sm md:text-base font-bold text-stone-900 truncate">Generate Ads</h1>
          <p className="text-xs text-stone-400 hidden sm:block">Select a profile, enter campaign details, and generate</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!countLoading && generationLimit && (
            <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-full whitespace-nowrap ${
              isGone ? 'bg-red-50 text-red-500 border border-red-200'
              : isLow ? 'bg-amber-50 text-amber-600 border border-amber-200'
              : 'bg-stone-100 text-stone-500'
            }`}>
              <i className={isGone ? 'ri-lock-2-line' : isLow ? 'ri-error-warning-line' : 'ri-flashlight-fill text-orange-400'} />
              <span className="hidden sm:inline">
                {isGone ? 'No generations left' : <><strong>{remaining}</strong> / {generationLimit} left</>}
              </span>
              <span className="sm:hidden font-bold">
                {isGone ? '0' : remaining}
              </span>
              {(isLow || isGone) && <Link to="/pricing" className="hidden sm:inline ml-1 underline underline-offset-2 hover:opacity-80 cursor-pointer">Upgrade</Link>}
            </div>
          )}
          {/* Clear / New Campaign button — only shown when a result exists */}
          {(result || sessionLoaded) && !isGenerating && (
            <>
              {showClearConfirm ? (
                <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-xl px-2.5 py-1.5">
                  <span className="text-xs text-red-600 font-medium whitespace-nowrap hidden sm:inline">Clear session?</span>
                  <button
                    onClick={() => { clearSession(); setShowClearConfirm(false); setGeneratedBusinessName(null); }}
                    className="text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-2 py-1 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              ) : result ? (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-stone-500 bg-stone-100 hover:bg-stone-200 border border-stone-200 px-3 py-1.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line" />
                  <span className="hidden sm:inline">Clear & Start New</span>
                </button>
              ) : null}
            </>
          )}
        </div>
      </header>

      {/* Alerts */}
      {proBulkCapExceeded && (
        <div className="mx-4 md:mx-6 mt-4 flex items-center gap-3 bg-amber-50 border border-amber-200 px-4 py-3 rounded-xl">
          <div className="w-8 h-8 flex items-center justify-center shrink-0 rounded-full bg-amber-100">
            <i className="ri-shield-line text-amber-500 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-stone-900">Daily Pro batch limit reached.</p>
            <p className="text-xs text-stone-500 mt-0.5">You can run up to 3 × 6-image Pro Creative batches per day. Resets at midnight.</p>
          </div>
          <button onClick={() => setProBulkCapExceeded(false)} className="text-stone-400 hover:text-stone-600 cursor-pointer shrink-0"><i className="ri-close-line text-lg" /></button>
        </div>
      )}
      {proBulkCooldown && (
        <div className="mx-4 md:mx-6 mt-4 flex items-center gap-3 bg-amber-50 border border-amber-200 px-4 py-3 rounded-xl">
          <div className="w-8 h-8 flex items-center justify-center shrink-0 rounded-full bg-amber-100">
            <i className="ri-time-line text-amber-500 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-stone-900">Cooldown active — {proBulkCooldown.remainingMin} min remaining.</p>
            <p className="text-xs text-stone-500 mt-0.5">6×Pro Creative batches have a 10-minute cooldown between runs.</p>
          </div>
          <button onClick={() => setProBulkCooldown(null)} className="text-stone-400 hover:text-stone-600 cursor-pointer shrink-0"><i className="ri-close-line text-lg" /></button>
        </div>
      )}
      {quotaExceeded && (
        <div className="mx-4 md:mx-6 mt-4 flex items-center gap-3 bg-amber-50 border border-amber-200 px-4 py-3 rounded-xl">
          <div className="w-8 h-8 flex items-center justify-center shrink-0 rounded-full bg-amber-100">
            <i className="ri-lock-2-line text-amber-500 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-stone-900">All free generations used.</p>
            <p className="text-xs text-stone-500 mt-0.5">Upgrade to continue generating unlimited ads.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setQuotaExceeded(false)} className="text-stone-400 hover:text-stone-600 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            <Link to="/pricing" className="flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-3 py-1.5 rounded-lg hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap">
              <i className="ri-flashlight-fill" /> Upgrade
            </Link>
          </div>
        </div>
      )}
      {error && (
        <div className="mx-4 md:mx-6 mt-4 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          <i className="ri-error-warning-line text-base shrink-0" />
          <span className="flex-1 text-xs md:text-sm">{error}</span>
          <button onClick={() => setError(null)} className="shrink-0 text-red-400 hover:text-red-600 cursor-pointer"><i className="ri-close-line" /></button>
        </div>
      )}
      {/* Session restored banner */}
      {sessionLoaded && result && !isGenerating && !loadedFromHistory && (
        <div className="mx-4 md:mx-6 mt-4 flex items-center gap-3 bg-orange-50 border border-orange-100 px-4 py-2.5 rounded-xl">
          <i className="ri-time-line text-orange-400 text-sm shrink-0" />
          <p className="text-xs text-stone-600 flex-1">
            <strong className="text-stone-800">Last session restored.</strong> Your ads and edits are right where you left them.
          </p>
          <button
            onClick={() => setShowClearConfirm(true)}
            className="text-xs text-stone-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            Clear
          </button>
        </div>
      )}

      {/* Loaded from history banner */}
      {loadedFromHistory && result && !isGenerating && (
        <div className="mx-4 md:mx-6 mt-4 flex items-center gap-3 bg-teal-50 border border-teal-100 px-4 py-2.5 rounded-xl">
          <i className="ri-history-line text-teal-500 text-sm shrink-0" />
          <p className="text-xs text-stone-600 flex-1">
            <strong className="text-stone-800">Loaded from history.</strong> Edit tools are ready below.
          </p>
          <button
            onClick={() => setLoadedFromHistory(false)}
            className="text-xs text-stone-400 hover:text-stone-600 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* ── Left: Form ─────────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-4">

            {/* Profile selector */}
            <ProfileSelector
              profiles={profiles}
              selectedId={selectedProfileId}
              onSelect={setSelectedProfileId}
              loading={profilesLoading}
            />

            {/* Creative presets */}
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-bold text-stone-900 flex items-center gap-2">
                    <i className="ri-bookmark-3-line text-orange-400 text-base" />
                    Creative Presets
                  </h2>
                  <p className="text-xs text-stone-400 mt-0.5">Reuse styles, direction, quality, and reference images</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowSavePreset((open) => !open);
                    setPresetMessage(null);
                  }}
                  className="shrink-0 flex items-center gap-1.5 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-xs font-bold text-stone-600 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-600 transition-all cursor-pointer"
                >
                  <i className="ri-add-line" />
                  Save
                </button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div className="flex gap-2">
                  <select
                    value={selectedPresetId}
                    onChange={(event) => setSelectedPresetId(event.target.value)}
                    disabled={presetsLoading || creativePresets.length === 0}
                    className={`${inputClass} cursor-pointer appearance-none`}
                  >
                    <option value="">
                      {presetsLoading
                        ? 'Loading presets...'
                        : creativePresets.length === 0
                          ? 'No saved presets yet'
                          : 'Choose a saved preset...'}
                    </option>
                    {creativePresets.map((preset) => (
                      <option key={preset.id} value={preset.id}>{preset.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => selectedPreset && applyPreset(selectedPreset)}
                    disabled={!selectedPreset}
                    className={`shrink-0 flex items-center justify-center gap-1.5 rounded-xl px-4 py-3 text-xs font-bold transition-all ${
                      selectedPreset
                        ? 'bg-stone-900 text-white hover:bg-stone-800 cursor-pointer'
                        : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                    }`}
                  >
                    <i className="ri-download-cloud-2-line" />
                    Apply
                  </button>
                </div>

                {selectedPreset && (
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold text-stone-800">{selectedPreset.name}</p>
                      <p className="truncate text-xs text-stone-400">
                        {selectedPreset.settings.campaign.platform || 'Any platform'}
                        {' · '}
                        {selectedPreset.settings.imageEngine === 'pro' ? 'Pro Creative' : 'Standard Creative'}
                        {selectedPreset.settings.referenceImageData ? ' · reference image' : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        await deletePreset(selectedPreset.id);
                        setSelectedPresetId('');
                        setPresetMessage(`Deleted "${selectedPreset.name}".`);
                      }}
                      className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-stone-400 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                      title="Delete preset"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                )}

                {showSavePreset && (
                  <div className="rounded-xl border border-orange-100 bg-orange-50 p-3 space-y-2">
                    <input
                      type="text"
                      value={presetName}
                      onChange={(event) => setPresetName(event.target.value.slice(0, 60))}
                      placeholder="Preset name, e.g. Weekly local promo"
                      className="w-full rounded-xl border border-orange-200 bg-white px-3 py-2.5 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={savePreset}
                        disabled={savingPreset}
                        className="flex items-center justify-center gap-1.5 rounded-xl bg-orange-500 px-3 py-2.5 text-xs font-extrabold text-white hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <i className={savingPreset ? 'ri-loader-4-line animate-spin' : 'ri-save-3-line'} />
                        {savingPreset ? 'Saving...' : 'Save Preset'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowSavePreset(false);
                          setPresetName('');
                          setPresetMessage(null);
                        }}
                        className="rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-xs font-bold text-stone-500 hover:border-stone-300 hover:text-stone-700 cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {(presetMessage || presetsError) && (
                  <p className={`text-xs ${presetsError ? 'text-red-500' : 'text-stone-500'}`}>
                    {presetsError || presetMessage}
                  </p>
                )}
              </div>
            </div>

            {/* Campaign form */}
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-100">
                <h2 className="text-sm font-bold text-stone-900">Campaign Details</h2>
                <p className="text-xs text-stone-400 mt-0.5">Set your offer, platform, and optional targeting</p>
              </div>

              <div className="px-5 py-5 space-y-4">

                {/* ── Offer ── always visible */}
                <div>
                  <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                    Offer / Promotion
                  </label>
                  <textarea
                    value={campaign.offer}
                    onChange={e => handleChange('offer', e.target.value)}
                    placeholder="e.g. $50 off first service, free estimate, 20% off this month, Buy 1 Get 1 free..."
                    rows={3}
                    maxLength={400}
                    className={`${inputClass} resize-none`}
                  />
                  <div className="text-right text-xs text-stone-400 mt-1">{campaign.offer.length}/400</div>
                </div>

                {/* ── Platform ── always visible */}
                <div>
                  <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                    Ad Platform <span className="text-orange-500">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {adPlatforms.map(platform => (
                      <button
                        key={platform}
                        onClick={() => handleChange('platform', platform)}
                        className={`py-2.5 rounded-xl text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          campaign.platform === platform
                            ? 'bg-orange-500 border-orange-500 text-white'
                            : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                        }`}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Targeting (collapsible) ── */}
                <CollapsibleSection
                  icon="ri-focus-3-line"
                  title="Targeting"
                  subtitle="Audience &amp; tone overrides"
                  badge={targetingBadge}
                >
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                      Target Audience
                      <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={campaign.audienceOverride}
                      onChange={e => handleChange('audienceOverride', e.target.value)}
                      placeholder={selectedProfile?.default_audience ? `Default: ${selectedProfile.default_audience}` : 'e.g. First-time homebuyers in Dallas'}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                      Tone Override
                      <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleChange('toneOverride', '')}
                        className={`px-3 py-2 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          campaign.toneOverride === ''
                            ? 'bg-stone-700 border-stone-700 text-white'
                            : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300'
                        }`}
                      >
                        Profile default
                      </button>
                      {toneOptions.map(t => (
                        <button
                          key={t.value}
                          onClick={() => handleChange('toneOverride', t.value)}
                          className={`px-3 py-2 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                            campaign.toneOverride === t.value
                              ? 'bg-orange-500 border-orange-500 text-white'
                              : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </CollapsibleSection>

                {/* ── Strategy (collapsible) ── */}
                <CollapsibleSection
                  icon="ri-compass-3-line"
                  title="Strategy"
                  subtitle="Goal &amp; ad angle"
                  badge={strategyBadge}
                >
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">Campaign Goal</label>
                    <select
                      value={campaign.campaignGoal}
                      onChange={e => handleChange('campaignGoal', e.target.value)}
                      className={`${inputClass} cursor-pointer appearance-none`}
                    >
                      <option value="">Select a goal (optional)...</option>
                      {campaignGoals.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                      Ad Angle
                      <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
                    </label>
                    <div className="grid grid-cols-1 gap-1.5">
                      <button
                        onClick={() => handleChange('adAngle', '')}
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border text-left text-xs font-semibold transition-all duration-200 cursor-pointer ${
                          campaign.adAngle === ''
                            ? 'bg-stone-700 border-stone-700 text-white'
                            : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300 hover:bg-stone-100'
                        }`}
                      >
                        <i className="ri-magic-line text-sm shrink-0" />
                        <div>
                          <div>AI decides</div>
                          <div className={`font-normal mt-0.5 ${campaign.adAngle === '' ? 'text-stone-300' : 'text-stone-400'}`}>Let the AI pick the best angle</div>
                        </div>
                      </button>
                      {adAngles.map(angle => (
                        <button
                          key={angle.value}
                          onClick={() => handleChange('adAngle', angle.value)}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border text-left text-xs font-semibold transition-all duration-200 cursor-pointer ${
                            campaign.adAngle === angle.value
                              ? 'bg-orange-500 border-orange-500 text-white'
                              : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:bg-orange-50/50'
                          }`}
                        >
                          <i className={`${angle.icon} text-sm shrink-0`} />
                          <div>
                            <div>{angle.label}</div>
                            <div className={`font-normal mt-0.5 ${campaign.adAngle === angle.value ? 'text-orange-100' : 'text-stone-400'}`}>{angle.desc}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </CollapsibleSection>

                {/* ── Creative (collapsible) ── */}
                <CollapsibleSection
                  icon="ri-palette-line"
                  title="Creative &amp; Notes"
                  subtitle="Image direction &amp; extra context"
                  badge={creativeBadge}
                >
                  {/* ── Reference Image (top priority) ── */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider">
                        Reference Image
                        <span className="normal-case font-normal text-orange-500 ml-1.5 tracking-normal">#1 Priority</span>
                      </label>
                      {referenceImageData && (
                        <button
                          onClick={() => { setReferenceImageData(null); setReferenceImagePreview(null); }}
                          className="text-xs text-stone-400 hover:text-red-500 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                        >
                          <i className="ri-delete-bin-line text-xs" /> Remove
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-stone-400 mb-2">
                      Upload an image you love. The AI will extract and match its exact visual style — overrides the style pack below.
                    </p>
                    <input
                      ref={referenceImageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleReferenceImageChange}
                      className="hidden"
                    />
                    {referenceImagePreview ? (
                      <div className="relative rounded-xl overflow-hidden border-2 border-orange-200">
                        <img
                          src={referenceImagePreview}
                          alt="Reference style"
                          className="w-full h-32 object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-3">
                          <span className="text-xs font-bold text-white flex items-center gap-1.5">
                            <i className="ri-checkbox-circle-fill text-orange-400" /> Style reference active
                          </span>
                        </div>
                        <button
                          onClick={() => referenceImageInputRef.current?.click()}
                          className="absolute top-2 right-2 bg-white/90 hover:bg-white text-stone-700 text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => referenceImageInputRef.current?.click()}
                        className="w-full flex flex-col items-center gap-2 py-5 px-4 rounded-xl border-2 border-dashed border-stone-200 hover:border-orange-300 hover:bg-orange-50/30 transition-all duration-200 cursor-pointer group"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-100 group-hover:bg-orange-100 transition-colors">
                          <i className="ri-image-add-line text-stone-400 group-hover:text-orange-400 text-lg transition-colors" />
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-semibold text-stone-600 group-hover:text-orange-600 transition-colors">Click to upload reference image</p>
                          <p className="text-xs text-stone-400 mt-0.5">JPG, PNG or WEBP</p>
                        </div>
                      </button>
                    )}
                  </div>

                  {/* ── Style Picker ── */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider">Image Style</label>
                      <span className="text-xs text-stone-400">{IMAGE_STYLES.length} styles available</span>
                    </div>

                    {effectiveCount === 1 ? (() => {
                      // ── Single select ──
                      const selectedStyleId = campaign.stylePackIds[0] ?? '';
                      const selectedStyle = selectedStyleId ? getImageStyleConfig(selectedStyleId) : null;
                      return (
                        <>
                          <button
                            onClick={() => setStyleModalOpen(true)}
                            className="w-full group relative overflow-hidden rounded-xl border-2 border-stone-200 hover:border-orange-400 transition-all duration-200 cursor-pointer text-left"
                          >
                            {selectedStyle ? (
                              <div className="flex items-center gap-0 h-16">
                                <div className="w-24 h-full shrink-0 overflow-hidden">
                                  <img src={selectedStyle.previewImage} alt={selectedStyle.label} className="w-full h-full object-cover object-top" />
                                </div>
                                <div className="flex-1 px-4">
                                  <p className="text-sm font-bold text-stone-900">{selectedStyle.label}</p>
                                  <p className="text-xs text-stone-400 mt-0.5">{selectedStyle.description}</p>
                                </div>
                                <div className="pr-4 flex items-center gap-1 text-xs font-semibold text-orange-500 group-hover:text-orange-600 whitespace-nowrap">
                                  <i className="ri-edit-line text-sm" /> Change
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-3 h-16 px-4">
                                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-100 shrink-0">
                                  <i className="ri-shuffle-line text-stone-500 text-base" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-bold text-stone-800">Auto Mix</p>
                                  <p className="text-xs text-stone-400">AI picks the best style for each ad</p>
                                </div>
                                <div className="flex items-center gap-1 text-xs font-semibold text-orange-500 group-hover:text-orange-600 whitespace-nowrap">
                                  <i className="ri-image-line text-sm" /> Choose Style
                                </div>
                              </div>
                            )}
                          </button>
                          {/* Quick-pick strip */}
                          <div className="flex gap-1.5 mt-2">
                            {IMAGE_STYLES.slice(0, 5).map((s) => (
                              <button
                                key={s.id}
                                onClick={() => setCampaign((prev) => ({ ...prev, stylePackIds: [s.id] }))}
                                title={s.label}
                                className={`relative flex-1 h-10 rounded-lg overflow-hidden border-2 transition-all duration-150 cursor-pointer ${
                                  selectedStyleId === s.id ? 'border-orange-500' : 'border-transparent hover:border-orange-300'
                                }`}
                              >
                                <img src={s.previewImage} alt={s.label} className="w-full h-full object-cover object-top" />
                                {selectedStyleId === s.id && (
                                  <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center">
                                    <i className="ri-check-line text-orange-600 text-xs font-bold" />
                                  </div>
                                )}
                              </button>
                            ))}
                            <button
                              onClick={() => setStyleModalOpen(true)}
                              className="flex-1 h-10 rounded-lg border-2 border-dashed border-stone-200 hover:border-orange-400 transition-all duration-150 cursor-pointer flex items-center justify-center text-stone-400 hover:text-orange-500"
                              title="View all styles"
                            >
                              <i className="ri-grid-line text-sm" />
                            </button>
                          </div>
                        </>
                      );
                    })() : (
                      // ── Multi-select (batch > 1) ──
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-stone-500">
                            {campaign.stylePackIds.length === 0
                              ? 'AI auto-mix for all images'
                              : `${campaign.stylePackIds.length} of ${effectiveCount} styles picked`}
                          </span>
                          {campaign.stylePackIds.length > 0 && (
                            <button
                              onClick={() => setCampaign((prev) => ({ ...prev, stylePackIds: [] }))}
                              className="text-xs text-stone-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        {/* Slot row */}
                        <div
                          className="grid gap-1.5"
                          style={{ gridTemplateColumns: `repeat(${effectiveCount}, 1fr)` }}
                        >
                          {Array.from({ length: effectiveCount }).map((_, i) => {
                            const styleId = campaign.stylePackIds[i];
                            const style = styleId ? getImageStyleConfig(styleId) : null;
                            return (
                              <button
                                key={i}
                                onClick={() => setStyleModalOpen(true)}
                                title={style ? style.label : `Image ${i + 1} — Auto`}
                                className={`relative h-14 rounded-lg overflow-hidden border-2 transition-all duration-150 cursor-pointer group ${
                                  style ? 'border-orange-400 hover:border-orange-500' : 'border-dashed border-stone-200 hover:border-orange-300 bg-stone-50'
                                }`}
                              >
                                {style ? (
                                  <>
                                    <img src={style.previewImage} alt={style.label} className="w-full h-full object-cover object-top" />
                                    <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-0.5">
                                      <span className="text-white text-xs font-extrabold leading-none">{i + 1}</span>
                                      <span className="text-white/80 text-xs leading-none truncate px-1 max-w-full">{style.label}</span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="w-full h-full flex flex-col items-center justify-center gap-0.5">
                                    <span className="text-stone-300 text-xs font-extrabold">{i + 1}</span>
                                    <span className="text-stone-300 text-xs leading-none">Auto</span>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          onClick={() => setStyleModalOpen(true)}
                          className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-dashed border-stone-200 hover:border-orange-400 text-xs font-semibold text-stone-400 hover:text-orange-500 transition-all cursor-pointer"
                        >
                          <i className="ri-palette-line text-sm" />
                          {campaign.stylePackIds.length === 0 ? `Pick styles for ${effectiveCount} images` : 'Edit style selection'}
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                      Creative Direction
                      <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
                    </label>
                    <p className="text-xs text-stone-400 mb-2">
                      Describe the scene or mood you want for the image. The AI uses this instead of auto-generating one.
                    </p>
                    <textarea
                      value={campaign.creativeDirection}
                      onChange={e => handleChange('creativeDirection', e.target.value)}
                      placeholder="e.g. Show a forestry mulcher clearing thick brush at sunrise, wide-angle shot with golden light..."
                      rows={3}
                      maxLength={400}
                      className={`${inputClass} resize-none`}
                    />
                    <div className="text-right text-xs text-stone-400 mt-1">{campaign.creativeDirection.length}/400</div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                      Campaign Notes
                      <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
                    </label>
                    <textarea
                      value={campaign.campaignNotes}
                      onChange={e => handleChange('campaignNotes', e.target.value)}
                      placeholder="Any extra context... e.g. Seasonal summer push targeting lapsed customers from 12+ months ago."
                      rows={2}
                      maxLength={400}
                      className={`${inputClass} resize-none`}
                    />
                  </div>
                </CollapsibleSection>

                {/* ── Batch Size (Pro: 1/3 · Agency: 1/3/6) ── */}
                {plan !== 'free' && (
                  <div className="rounded-xl border border-stone-200 bg-stone-50 px-4 py-3">
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-gallery-line text-stone-500 text-sm" />
                        </div>
                        <span className="text-xs font-bold text-stone-700 uppercase tracking-wider">Batch Size</span>
                        <span className="text-xs text-stone-400 font-normal normal-case tracking-normal capitalize">{plan}</span>
                      </div>
                    </div>
                    <div className={`grid gap-2 ${PLAN_BATCH_OPTIONS[plan].length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                      {PLAN_BATCH_OPTIONS[plan].map(count => {
                        const isSelected = campaign.imageCount === count;
                        const batchCost = getBulkCreditCost(imageEngine, count);
                        const isProBulk6 = count === 6 && imageEngine === 'pro';
                        return (
                          <button
                            key={count}
                            onClick={() => setCampaign(prev => ({ ...prev, imageCount: count }))}
                            className={`relative flex flex-col items-center gap-0.5 py-2.5 px-2 rounded-xl border-2 text-xs font-bold transition-all duration-200 cursor-pointer ${
                              isSelected
                                ? 'bg-orange-500 border-orange-500 text-white'
                                : 'bg-white border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                            }`}
                          >
                            <span className="text-sm font-extrabold">{count} image{count !== 1 ? 's' : ''}</span>
                            <span className={`font-semibold ${isSelected ? 'text-orange-100' : 'text-stone-400'}`}>
                              {batchCost} cr
                            </span>
                            {isProBulk6 && (
                              <span className={`text-xs font-bold px-1 py-0.5 rounded-full mt-0.5 ${isSelected ? 'bg-orange-400 text-orange-100' : 'bg-amber-100 text-amber-700'}`}>
                                capped
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {campaign.imageCount > 1 && (
                      <div className="mt-2 flex items-center gap-2 rounded-xl border border-orange-100 bg-orange-50 px-3 py-2 text-xs text-orange-800">
                        <i className="ri-vip-crown-line text-sm shrink-0" />
                        <span className="flex-1">Batch generation is best for paid campaign testing.</span>
                        <Link
                          to="/pricing"
                          onClick={() => trackEvent('upgrade_clicked', { source: 'generate_batch_size', batch_size: campaign.imageCount })}
                          className="font-bold underline underline-offset-2"
                        >
                          Upgrade
                        </Link>
                      </div>
                    )}
                    {imageEngine === 'pro' && campaign.imageCount === 6 && plan === 'agency' && (
                      <p className="text-xs text-amber-600 mt-2 flex items-start gap-1.5">
                        <i className="ri-shield-line shrink-0 mt-0.5" />
                        <span>Limited to 3 batches/day · 10 min cooldown between runs</span>
                      </p>
                    )}
                  </div>
                )}

                {/* ── Image Quality / Creative Mode ── */}
                <div className="rounded-xl border border-stone-200 overflow-hidden">
                  <div className="px-4 py-3 bg-stone-50 border-b border-stone-100">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-sparkling-line text-orange-400 text-sm" />
                      </div>
                      <span className="text-xs font-bold text-stone-700 uppercase tracking-wider">Image Quality</span>
                      <span className="text-xs text-stone-400 font-normal normal-case tracking-normal">Creative Mode</span>
                    </div>
                  </div>
                  <div className="px-4 py-3 bg-white space-y-2">
                    {IMAGE_ENGINES.map((engine) => {
                      const isSelected = imageEngine === engine.id;
                      return (
                        <button
                          key={engine.id}
                          onClick={() => setImageEngine(engine.id)}
                          className={`relative w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                            isSelected
                              ? 'border-orange-400 bg-orange-50'
                              : 'border-stone-200 bg-stone-50 hover:border-stone-300 hover:bg-white'
                          }`}
                        >
                          {/* Radio indicator */}
                          <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                            isSelected ? 'border-orange-500 bg-orange-500' : 'border-stone-300 bg-white'
                          }`}>
                            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </div>
                          {/* Icon */}
                          <div className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                            isSelected ? 'bg-orange-500 text-white' : 'bg-stone-200 text-stone-500'
                          }`}>
                            <i className={`${engine.icon} text-sm`} />
                          </div>
                          {/* Text */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs font-bold ${isSelected ? 'text-orange-800' : 'text-stone-800'}`}>
                                {engine.label}
                              </span>
                              {engine.badge === 'recommended' && (
                                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                                  Recommended
                                </span>
                              )}
                              {engine.badge === 'best-quality' && (
                                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                                  Best Quality
                                </span>
                              )}
                            </div>
                            <p className={`text-xs mt-0.5 leading-tight ${isSelected ? 'text-orange-600' : 'text-stone-400'}`}>
                              {engine.tagline}
                            </p>
                          </div>
                          {/* Credit cost — reflects current batch size */}
                          <div className="shrink-0 text-right">
                            <span className={`text-sm font-extrabold ${isSelected ? 'text-orange-600' : 'text-stone-700'}`}>
                              {getBulkCreditCost(engine.id, effectiveCount)}
                            </span>
                            <span className={`text-xs ml-0.5 ${isSelected ? 'text-orange-400' : 'text-stone-400'}`}>cr</span>
                          </div>
                        </button>
                      );
                    })}
                    <p className="text-xs text-stone-400 pt-1 flex items-center gap-1.5">
                      <i className="ri-information-line text-stone-300" />
                      {getEngineConfig(imageEngine).recommendedUse}
                    </p>
                    {imageEngine === 'pro' && (
                      <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        <i className="ri-sparkling-2-line text-sm shrink-0" />
                        <span className="flex-1">Pro Creative uses more credits for premium layouts and text rendering.</span>
                        <Link
                          to="/pricing"
                          onClick={() => trackEvent('upgrade_clicked', { source: 'generate_high_quality' })}
                          className="font-bold underline underline-offset-2"
                        >
                          See plans
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Generate buttons ── */}
                {(() => {
                  const creditCost = effectiveCreditCost;
                  const enoughCredits = hasEnoughCredits(creditCost);
                  const buttonDisabled = !isValid || isGenerating || !enoughCredits;

                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between px-1">
                        <span className="text-xs text-stone-400">Your balance</span>
                        <span className={`text-xs font-bold ${enoughCredits ? 'text-stone-700' : 'text-red-500'}`}>
                          {creditsRemaining} credits
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-xs">
                        <span className="font-semibold text-stone-500">Cost before generation</span>
                        <span className="font-extrabold text-stone-900">{creditCost} credits</span>
                      </div>
                      
                      {/* Primary: Generate Image Ads */}
                      <button
                        onClick={run}
                        disabled={buttonDisabled}
                        className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-sm transition-all duration-200 whitespace-nowrap ${
                          !buttonDisabled
                            ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                            : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                        }`}
                      >
                        {isGenerating ? (
                          <><i className="ri-loader-4-line text-base animate-spin" /> Generating your ads...</>
                        ) : result ? (
                          <><i className="ri-refresh-line text-base" /> Regenerate — {creditCost} Credits</>
                        ) : (
                          <><i className="ri-flashlight-fill text-base" /> Generate Ad — {creditCost} Credits</>
                        )}
                      </button>
                      {isGenerating && (
                        <button
                          type="button"
                          onClick={stopGeneration}
                          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-stone-300 bg-white text-stone-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700 font-bold text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-stop-circle-line text-base" />
                          Stop and edit form
                        </button>
                      )}
                      
                      <Link
                        to="/dashboard/video-ads"
                        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border-2 border-stone-200 bg-white text-stone-700 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 font-bold text-sm transition-all duration-200 whitespace-nowrap"
                      >
                        <i className="ri-movie-2-line text-base" />
                        Open Video Ads
                      </Link>
                      
                      {!enoughCredits && !isGenerating && (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-xl">
                          <i className="ri-error-warning-line text-sm shrink-0" />
                          <span className="flex-1">Not enough credits. Please upgrade or purchase more.</span>
                          <Link
                            to="/pricing"
                            onClick={() => trackEvent('upgrade_clicked', { source: 'generate_insufficient_credits' })}
                            className="shrink-0 text-xs font-bold text-red-600 underline underline-offset-2 whitespace-nowrap cursor-pointer"
                          >
                            Upgrade
                          </Link>
                        </div>
                      )}
                      {enoughCredits && creditsRemaining <= Math.max(creditCost, 25) && !isGenerating && (
                        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2.5 rounded-xl">
                          <i className="ri-flashlight-line text-sm shrink-0" />
                          <span className="flex-1">Your balance is getting low. Add credits before the next campaign.</span>
                          <Link
                            to="/pricing"
                            onClick={() => trackEvent('upgrade_clicked', { source: 'generate_low_credits' })}
                            className="shrink-0 text-xs font-bold text-amber-900 underline underline-offset-2 whitespace-nowrap cursor-pointer"
                          >
                            Add credits
                          </Link>
                        </div>
                      )}
                      <div className="flex items-center justify-center gap-1.5 text-xs text-stone-400">
                        <i className="ri-image-line text-stone-300" />
                        <span>
                          {effectiveCount} image{effectiveCount !== 1 ? 's' : ''}{' · '}
                          <strong className="text-stone-600">{creditCost} credits</strong>
                        </span>
                      </div>
                    </div>
                  );
                })()}

              </div>
            </div>
          </div>

          {/* ── Right: Results ─────────────────────────────────────── */}
          <div className="lg:col-span-3">
            <ResultsPanel
              result={result}
              isGenerating={isGenerating}
              logoUrl={selectedProfile?.logo_url ?? undefined}
              onRegenerate={run}
              onRegenerateCopy={result ? handleRegenerateCopy : undefined}
              onSave={async () => {
                if (!generationId || !result) return;
                const baseUrls = [...(result.imageStorageUrls ?? result.imageUrls ?? [])];
                Object.entries(editedUrls).forEach(([idx, url]) => { baseUrls[Number(idx)] = url; });
                await supabase.from('ad_generations').update({ image_storage_urls: baseUrls }).eq('id', generationId);
              }}
              platform={campaign.platform}
              businessName={generatedBusinessName ?? selectedProfile?.business_name ?? 'your business'}
              editedUrls={editedUrls}
              editHistory={editHistory}
              onEditedUrlUpdate={updateEditedUrl}
              onEditHistoryUpdate={updateEditHistory}
              onRevertToOriginal={revertToOriginal}
              onClearSession={clearSession}
              onRegenerateSingle={result ? handleRegenerateSingle : undefined}
              adsPerBatch={effectiveCount}
              businessContext={{
                businessName: selectedProfile?.business_name,
                businessType: selectedProfile?.business_type,
                businessDescription: selectedProfile?.business_description,
                servicesOffered: selectedProfile?.services_offered,
                offer: campaign.offer,
                platform: campaign.platform,
                tone: campaign.toneOverride || selectedProfile?.default_tone,
                audience: campaign.audienceOverride || selectedProfile?.default_audience,
                location: selectedProfile?.location,
              }}
            />
          </div>

        </div>
      </div>
    </div>
  );
}
